import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { parseUtc } from '@/utils/datetime'

export function useExam() {
  const store = useStore()
  const examFilter = ref('all')
  const currentStep = ref(1)

  // Getters
  const exams = computed(() => store.getters['exam/getExams'])
  const currentExam = computed(() => store.getters['exam/getCurrentExam'])
  const examSession = computed(() => store.getters['exam/getExamSession'])
  const monitoringData = computed(() => store.getters['exam/getMonitoringData'])
  const statistics = computed(() => store.getters['exam/getStatistics'])
  const gradingResults = computed(() => store.getters['exam/getGradingResults'])
  const reports = computed(() => store.getters['exam/getReports'])
  const isLoading = computed(() => store.getters['exam/isLoading'])
  const error = computed(() => store.getters['exam/getError'])
  const examProgress = computed(() => store.getters['exam/getExamProgress'])

  // Filtered exams
  const filteredExams = computed(() => {
    if (examFilter.value === 'all') return exams.value
    return store.getters['exam/getExamsByStatus'](examFilter.value)
  })

  const activeExams = computed(() => store.getters['exam/getActiveExams'])
  const scheduledExams = computed(() => store.getters['exam/getScheduledExams'])
  const completedExams = computed(() => store.getters['exam/getCompletedExams'])

  // Actions
  const fetchExams = async (params = {}) => {
    return await store.dispatch('exam/fetchExams', params)
  }

  const fetchExam = async (examId) => {
    return await store.dispatch('exam/fetchExam', examId)
  }

  const createExam = async (examData) => {
    return await store.dispatch('exam/createExam', examData)
  }

  const updateExam = async (examId, examData) => {
    return await store.dispatch('exam/updateExam', { examId, examData })
  }

  const updateExamStatus = async (examId, status) => {
    return await store.dispatch('exam/updateExamStatus', { examId, status })
  }

  const deleteExam = async (examId) => {
    return await store.dispatch('exam/deleteExam', examId)
  }

  const copyExam = async (sourceExamId, newExamName) => {
    return await store.dispatch('exam/copyExam', { sourceExamId, newExamName })
  }

  const shareExam = async (shareData) => {
    return await store.dispatch('exam/shareExam', shareData)
  }

  const getShareSettings = async (examId) => {
    return await store.dispatch('exam/getShareSettings', examId)
  }

  const updateShareSettings = async (examId, shareData) => {
    return await store.dispatch('exam/updateShareSettings', { examId, shareData })
  }

  const revokeShare = async (examId) => {
    return await store.dispatch('exam/revokeShare', examId)
  }

  const fetchSharedExams = async (params) => {
    return await store.dispatch('exam/fetchSharedExams', params)
  }

  // 나에게 공유된 시험 목록 조회 (지정 공유 방식)
  const fetchSharedExamsToMe = async () => {
    return await store.dispatch('exam/fetchSharedExamsToMe')
  }

  // 공유 대상자 수정
  const updateShareRecipients = async (shareId, recipients) => {
    return await store.dispatch('exam/updateShareRecipients', { shareId, recipients })
  }

  const accessSharedExam = async (shareCode) => {
    return await store.dispatch('exam/accessSharedExam', shareCode)
  }

  const fetchExamClassroom = async (examId) => {
    return await store.dispatch('exam/fetchExamClassroom', examId)
  }

  const fetchExamStudents = async (classroomIdx, examId) => {
    return await store.dispatch('exam/fetchExamStudents', { classroomIdx, examId })
  }

  const fetchAttendance = async (examId) => {
    return await store.dispatch('exam/fetchAttendance', examId)
  }

  const saveAttendance = async (attendanceData) => {
    return await store.dispatch('exam/saveAttendance', attendanceData)
  }

  const clearAttendance = async (examId, classroomIdx) => {
    return await store.dispatch('exam/clearAttendance', { examId, classroomIdx })
  }

  const startExam = async (examId, classroomIdx) => {
    return await store.dispatch('exam/startExam', { examId, classroomIdx })
  }

  const generateReport = async (examId) => {
    return await store.dispatch('exam/generateReport', examId)
  }

  const fetchExamReport = async (examId) => {
    return await store.dispatch('exam/fetchExamReport', examId)
  }

  const downloadReport = async (examId) => {
    return await store.dispatch('exam/downloadReport', examId)
  }

  // WebSocket management
  const connectWebSocket = (examId) => {
    store.dispatch('exam/connectWebSocket', examId)
  }

  const disconnectWebSocket = () => {
    store.dispatch('exam/disconnectWebSocket')
  }

  const resetMonitoringData = () => {
    store.dispatch('exam/resetMonitoringData')
  }


  // Exam creation wizard
  const wizardSteps = [
    { id: 1, name: '기본 정보', icon: 'info' },
    { id: 2, name: '시험 설정', icon: 'settings' },
    { id: 3, name: '문제 분포', icon: 'pie_chart' },
    { id: 4, name: '문제 선택', icon: 'checklist' },
    { id: 5, name: '검토 및 생성', icon: 'task_alt' }
  ]

  const nextStep = () => {
    if (currentStep.value < wizardSteps.length) {
      currentStep.value++
    }
  }

  const previousStep = () => {
    if (currentStep.value > 1) {
      currentStep.value--
    }
  }

  const goToStep = (step) => {
    if (step >= 1 && step <= wizardSteps.length) {
      currentStep.value = step
    }
  }

  // Exam validation
  const validateExamData = (examData) => {
    const errors = []

    if (!examData.examName || examData.examName.trim().length < 3) {
      errors.push('시험명은 최소 3자 이상이어야 합니다.')
    }

    if (!examData.duration || examData.duration < 10 || examData.duration > 300) {
      errors.push('시험 시간은 10분에서 300분 사이여야 합니다.')
    }

    if (!examData.startTime) {
      errors.push('시험 시작 시간을 설정해주세요.')
    }

    // 안전한 날짜 비교 (브라우저간 파싱 오차 방지)
    const parseLocalDateTime = (value) => {
      if (!value) return NaN
      // datetime-local 형식(YYYY-MM-DDTHH:mm[ss])을 신뢰하고 로컬 타임존으로 파싱
      const d = new Date(value)
      const t = d.getTime()
      return Number.isNaN(t) ? NaN : t
    }

    const startTs = parseLocalDateTime(examData.startTime)
    // 종료시간은 시작시간 + duration(분)으로 계산
    const endTs   = (!Number.isNaN(startTs) && examData.duration)
      ? (startTs + Number(examData.duration) * 60000)
      : NaN

    if (!Number.isNaN(startTs) && !Number.isNaN(endTs)) {
      if (startTs >= endTs) {
        errors.push('종료 시간은 시작 시간보다 늦어야 합니다.')
      }
    }

    if (!examData.textBooks || examData.textBooks.length === 0) {
      errors.push('최소 1개 이상의 교재를 선택해주세요.')
    }

    // Validate question distribution
    const distribution = examData.questionDistribution
    if (distribution) {
      let totalQuestions = 0
      Object.values(distribution).forEach(skill => {
        if (skill.count) totalQuestions += skill.count
      })

      if (totalQuestions === 0) {
        errors.push('최소 1개 이상의 문제를 배치해주세요.')
      }
    }

    // 선택된 문제 중 score가 0인 문제 체크
    const selectedQuestions = examData.selectedQuestions
    if (selectedQuestions) {
      const zeroScoreQuestions = []
      Object.entries(selectedQuestions).forEach(([skill, questions]) => {
        if (Array.isArray(questions)) {
          questions.forEach((q, idx) => {
            if (!q.score || Number(q.score) === 0) {
              zeroScoreQuestions.push(`${skill} ${idx + 1}번 문제`)
            }
          })
        }
      })
      if (zeroScoreQuestions.length > 0) {
        errors.push(`점수가 0인 문제가 있습니다. 점수를 입력해주세요: ${zeroScoreQuestions.join(', ')}`)
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Generate exam ID for session (for backward compatibility, returns exam ID format now)
  const generateExamNumber = () => {
    // This function is deprecated - sessions now use actual exam IDs
    console.warn('generateExamNumber is deprecated. Use actual exam IDs instead.')
    return null
  }

  // Format exam status
  const formatExamStatus = (status) => {
    const statusNames = {
      draft: '초안',
      scheduled: '예정',
      active: '진행중',
      completed: '완료'
    }
    return statusNames[status] || status
  }

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      draft: 'secondary',
      scheduled: 'warning',
      active: 'success',
      completed: 'info'
    }
    return colors[status] || 'default'
  }

  // Calculate exam statistics
  const calculateStatistics = (gradingResults) => {
    if (!gradingResults || gradingResults.length === 0) {
      return {
        average: 0,
        highest: 0,
        lowest: 0,
        median: 0,
        passRate: 0
      }
    }

    const scores = gradingResults.map(r => r.totalScore ?? 0).sort((a, b) => a - b)
    const average = scores.reduce((sum, score) => sum + score, 0) / scores.length
    const highest = Math.max(...scores)
    const lowest = Math.min(...scores)
    const median = scores[Math.floor(scores.length / 2)]
    const passRate = (scores.filter(s => s >= 60).length / scores.length) * 100

    return {
      average: average.toFixed(1),
      highest,
      lowest,
      median,
      passRate: passRate.toFixed(1)
    }
  }

  // Format time remaining
  const formatTimeRemaining = (endTime) => {
    const now = new Date()
    const end = parseUtc(endTime)
    if (!end) return '-'
    const diff = end - now

    if (diff <= 0) return '시간 종료'

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    if (hours > 0) {
      return `${hours}시간 ${minutes}분 남음`
    } else if (minutes > 0) {
      return `${minutes}분 ${seconds}초 남음`
    } else {
      return `${seconds}초 남음`
    }
  }

  return {
    // State
    exams,
    currentExam,
    examSession,
    monitoringData,
    statistics,
    gradingResults,
    reports,
    isLoading,
    error,
    examFilter,
    filteredExams,
    activeExams,
    scheduledExams,
    completedExams,
    examProgress,
    
    // Wizard
    currentStep,
    wizardSteps,
    nextStep,
    previousStep,
    goToStep,
    
    // Actions
    fetchExams,
    fetchExam,
    createExam,
    updateExam,
    updateExamStatus,
    deleteExam,
    copyExam,
    shareExam,
    getShareSettings,
    updateShareSettings,
    updateShareRecipients,
    revokeShare,
    fetchSharedExams,
    fetchSharedExamsToMe,
    accessSharedExam,
    fetchExamClassroom,
    fetchExamStudents,
    fetchAttendance,
    saveAttendance,
    clearAttendance,
    startExam,
    generateReport,
    fetchExamReport,
    downloadReport,
    connectWebSocket,
    disconnectWebSocket,
    resetMonitoringData,

    // Utilities
    validateExamData,
    generateExamNumber,
    formatExamStatus,
    getStatusColor,
    calculateStatistics,
    formatTimeRemaining
  }
}